import { Component, OnInit, Output, EventEmitter, Inject, OnDestroy } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { GenericResponse, Mobile, Select } from '@interfaces';
import { I18NextService, I18NEXT_SERVICE } from 'angular-i18next';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AuthService } from 'src/app/auth';
import { CampaignDetail, CampaignLevelsFees, Fee } from 'src/app/modules/campaigns/models/campaign.models';
import { CampaignsService } from 'src/app/modules/campaigns/services/campaigns.service';
import { UtilService } from 'src/app/services/util.service';
import { CalculateEnterMargins, ContactPerson, InstallationMultiPoint, InstallationsMultiPoint, SignerMultiPoint, MultipointGetCampaignRequest } from '../../new-contract-pymes-multipoint.model';
import { NewContractPymesMultipointService } from '../../new-contract-pymes-multipoint.service';
import { ToastrService } from '@shared/ui/toastr/toastr.service';
import { UtilValidatorsService } from 'src/app/commons/validations-format/util-validators.service';
import { NewContractPymesService } from '@services/new-contract-pymes.service';
import { validateIBANPortugal } from 'src/app/commons/utils';

@Component({
  selector: 'app-multipoint-signer-contract',
  templateUrl: './multipoint-signer-contract.component.html',
  styleUrls: [ './multipoint-signer-contract.component.scss' ]
})
export class MultipointSignerContractComponent implements OnInit, OnDestroy {
  private destroyed$: Subject<boolean> = new Subject<boolean>();
  private destroyed2$: Subject<boolean> = new Subject<boolean>();
  public campaignList: Select[] = [];
  public campaignRes = [];
  public levelList: Select[] = [];
  private contact: ContactPerson;
  public signerForm: FormGroup;
  public isFixedPrice = false;
  public campaignDetail: CampaignDetail;
  public regexTwoDecimal = new RegExp(/^(\d{1,2}(\,\d{0,3})?|\.?\d{1,2})$/g);
  public regexSixDecimal = new RegExp(/^(\d+(\,\d{0,6})?|\,?\d{1,3})$/g);
  public showCalculate = false;
  public calculates: CalculateEnterMargins[] = [];
  public unitsTE: Select[] = [
    { value: '0', label: '€ / kWh' },
    { value: '100', label: 'ctms / kWh' },
    { value: '1000', label: '€ / MWh' }
  ];
  public resultCalcTE: number;
  public resultCalcTP: number;
  public marginCups: Select[] = [];
  public unitsTP: Select[] = [];
  public installations: InstallationMultiPoint[] = [];
  public commission = 0;
  public canSettlements: boolean;
  public isUserSpanish = true;
  public contactMobileCountryCodeList: Select[] = [];
  public passThrough: boolean;
  public fijoFlex: boolean;
  public greaterEqual100: boolean;
  public today = new Date();
  private idDraft: number;
  public geographicZones: string[] = [];
  public geographicIndexTab: number = 0;
  private enterMarginsValid = false;
  public showBankAccountFields = false;
  public fijoFlexSimple: boolean = false;

  @Output() changeLoader: EventEmitter<boolean> = new EventEmitter();
  @Output() data: EventEmitter<SignerMultiPoint> = new EventEmitter();
  @Output() installationData: EventEmitter<InstallationsMultiPoint> = new EventEmitter();

  constructor(
    private newContractPymesMultipointService: NewContractPymesMultipointService,
    private newContractPymesService: NewContractPymesService,
    private utilService: UtilService,
    private campaignsService: CampaignsService,
    private authService: AuthService,
    private toastrService: ToastrService,
    @Inject(I18NEXT_SERVICE) private i18NextService: I18NextService,
    private utilValidatorsService: UtilValidatorsService
  ) { }

  ngOnInit(): void {
    this.today.setDate(this.today.getDate() + 1);
    this.isUserSpanish = this.authService.getUserData().country === 'ES';
    this.buildForm();
    this.setValidatorsDocumentNumber();
    this.initMobileCountryCodePT();
    this.changeLoader.emit(true);
    this.canSettlements = this.authService.getUserData().hasAccessToSettlement;
    this.getValuesForm();
    this.i18NextService.events.languageChanged.pipe(takeUntil(this.destroyed$)).subscribe(() => {
      this.getDropdowns();
    });
    this.subscribeRadioDateChange();
    if (!this.isUserSpanish) {
      this.subscribePaymentChanges();
      this.subscribeSameBankAccount();
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  private setValidatorsDocumentNumber(): void {
    if (this.isUserSpanish) {
      this.signerForm.get('contactDocumentNumber').setValidators([ Validators.required, this.utilValidatorsService.validateCIFNIF ]);
    } else {
      this.signerForm.get('contactDocumentNumber').setValidators([ Validators.required, this.utilValidatorsService.validateDNI ]);
    }
  }

  private calculateMargins(): FormGroup {
    if (this.isUserSpanish) {
      return new FormGroup({
        marginTP: new FormGroup({
          p1: new FormControl(null),
          p2: new FormControl(null),
          p3: new FormControl(null),
          p4: new FormControl(null),
          p5: new FormControl(null),
          p6: new FormControl(null)
        }),
        marginTE: new FormGroup({
          p1: new FormControl(null),
          p2: new FormControl(null),
          p3: new FormControl(null),
          p4: new FormControl(null),
          p5: new FormControl(null),
          p6: new FormControl(null)
        })
      });
    } else {
      return new FormGroup({
        marginTE: new FormGroup({
          p1: new FormControl(null),
          p2: new FormControl(null),
          p3: new FormControl(null),
          p4: new FormControl(null)
        })
      });
    }
  }

  private enterMargins(): FormGroup {
    if (this.isUserSpanish) {
      return new FormGroup({
        marginTP: new FormControl(null),
        marginsTE: new FormControl(null)
      });
    } else {
      return new FormGroup({
        marginsTE: new FormControl(null)
      });
    }
  }

  private getDropdowns(): void {
    this.unitsTP = [
      { value: '0', label: this.i18NextService.t('NEW_CONTRACTS.KW_YEAR').toString() },
      { value: '12', label: this.i18NextService.t('NEW_CONTRACTS.KW_MONTH').toString() },
      { value: '365', label: this.i18NextService.t('NEW_CONTRACTS.KW_DAY').toString() }
    ];
  }

  private getValuesForm(): void {
    this.newContractPymesMultipointService.getSavedDataMultipoint().pipe(takeUntil(this.destroyed2$)).subscribe(res => {
      const signerMobileAndCountryCode = this.getMobileAndCountryCode(res?.signer?.contactMobile);
      this.signerForm.patchValue({
        contactName: res?.signer?.contactName || null,
        contactSurname: res?.signer?.contactSurname || null,
        contactDocumentNumber: res?.signer?.contactDocumentNumber || '',
        contactEmail: res?.signer?.contactEmail || null,
        contactMobileCountryCode: this.isUserSpanish ? null : signerMobileAndCountryCode.countryCode || '',
        contactMobile: signerMobileAndCountryCode.mobile || '',
        campaign: res?.signer?.campaign || null,
        level: res?.signer?.level || null,
        marginIndicator: res?.signer?.marginIndicator || '1',
        unitsTE: res?.signer?.unitsTE || '0',
        unitsTP: res?.signer?.unitsTP || '0',
        campaignBase: res?.signer?.campaignBase || null,
        marginCups: res?.signer?.marginCups || null,
        invoiceDate: res?.signer?.invoiceDate || null,
        payment: res?.signer?.isMultibanco ? '1' : '0',
        sameBankAccount: res?.signer?.sameBankAccount || '0',
      });
      if (res?.signer?.invoiceDate) {
        this.signerForm.get('radioDate').setValue('2');
      } else {
        this.signerForm.get('radioDate').setValue('1');
      }
      this.replaceCalculateMargins(res?.signer?.calculateMargins);
      this.replaceEnterMargins(res?.signer?.enterMargins);
      const businessMobileAndCountryCode = this.getMobileAndCountryCode(res?.business?.mobile);
      this.contact = {
        contactName: res?.business?.contactName,
        contactSurname: res?.business?.surname,
        contactDocumentNumber: res?.business?.dni,
        contactEmail: res?.business?.email,
        contactMobile: businessMobileAndCountryCode.mobile
      };
      if (!this.isUserSpanish && businessMobileAndCountryCode.countryCode) {
        this.contact.contactMobileCountryCode = businessMobileAndCountryCode.countryCode;
      }
      this.installations = res.installations;
      if (!this.isUserSpanish) {
        this.builPTBankAccountFields();
        if (res?.signer?.campaign) {
          this.signerForm.get('sameBankAccount').enable();
        }
      }
      this.geographicZones = [ ... new Set(this.installations.map(installation => installation.geographicZone)) ].filter(val => !!val);
      this.marginCups = this.installations.map(val => ({ value: val.cups, label: val.cups }));
      this.idDraft = res?.idDraft;
      this.getCampaigns();
      this.greaterEqual100 = res.installations.some(installation => installation.consumptionEstimated >= 100000);
      this.destroyed2$.next();
      this.destroyed2$.complete();
    });
  }

  private builPTBankAccountFields(): void {
    Object.keys(this.signerForm.controls).filter(val => val.startsWith('iban')).forEach(controlName => {
      this.signerForm.removeControl(controlName);
    })
    if (this.signerForm.get('sameBankAccount').value !== '0') {
      this.installations?.forEach((installation, index) => {
      this.signerForm.addControl('ibanCode' + '_' + index, new FormControl({ value: installation?.ibanCode || null, disabled: false }));
      this.signerForm.addControl('ibanEntity' + '_' + index, new FormControl({ value: installation?.ibanEntity, disabled: false }));
      this.signerForm.addControl('ibanOffice' + '_' + index, new FormControl({ value: installation?.ibanOffice, disabled: false }));
      this.signerForm.addControl('ibanDC' + '_' + index, new FormControl({ value: installation?.ibanDC, disabled: false }));
      this.signerForm.addControl('ibanAccount' + '_' + index, new FormControl({ value: installation?.ibanAccount, disabled: false }));
      });
    } else {
      this.signerForm.addControl('ibanCode_0', new FormControl({ value: this.installations[0]?.ibanCode || null, disabled: false }));
      this.signerForm.addControl('ibanEntity_0', new FormControl({ value: this.installations[0]?.ibanEntity, disabled: false }));
      this.signerForm.addControl('ibanOffice_0', new FormControl({ value: this.installations[0]?.ibanOffice, disabled: false }));
      this.signerForm.addControl('ibanDC_0', new FormControl({ value: this.installations[0]?.ibanDC, disabled: false }));
      this.signerForm.addControl('ibanAccount_0', new FormControl({ value: this.installations[0]?.ibanAccount, disabled: false }));
    }
    this.signerForm.updateValueAndValidity();
  }

  private calculatedDone(): void {
    if (Object.values(this.signerForm.get('enterMargins').value).every(val => val) ||
      (Object.values(this.signerForm.get('calculateMargins.marginTE').value).every(val => val) &&
        Object.values(this.signerForm.get('calculateMargins.marginTP').value).every(val => val) &&
        this.signerForm.get('marginCups').value
      )
    ) {
      this.showCalculate = true;
      this.handleCalculate();
    }
  }

  private replaceEnterMargins(object): void {
    // replace Enter Margins
    this.signerForm.get('enterMargins').patchValue({
      marginTP: object?.marginTP ? object.marginTP.toString().replace('.', ',') : null,
      marginTE: object?.marginTE ? object.marginTE.toString().replace('.', ',') : null
    });
  }

  private replaceCalculateMargins(object): void {
    if (this.isUserSpanish) {
      this.signerForm.get('calculateMargins.marginTE').patchValue({
        p1: object?.marginTE?.p1 ? object.marginTE.p1.toString().replace('.', ',') : null,
        p2: object?.marginTE?.p2 ? object.marginTE.p2.toString().replace('.', ',') : null,
        p3: object?.marginTE?.p3 ? object.marginTE.p3.toString().replace('.', ',') : null,
        p4: object?.marginTE?.p4 ? object.marginTE.p4.toString().replace('.', ',') : null,
        p5: object?.marginTE?.p5 ? object.marginTE.p5.toString().replace('.', ',') : null,
        p6: object?.marginTE?.p6 ? object.marginTE.p6.toString().replace('.', ',') : null
      });
      this.signerForm.get('calculateMargins.marginTP').patchValue({
        p1: object?.marginTP?.p1 ? object.marginTP.p1.toString().replace('.', ',') : null,
        p2: object?.marginTP?.p2 ? object.marginTP.p2.toString().replace('.', ',') : null,
        p3: object?.marginTP?.p3 ? object.marginTP.p3.toString().replace('.', ',') : null,
        p4: object?.marginTP?.p4 ? object.marginTP.p4.toString().replace('.', ',') : null,
        p5: object?.marginTP?.p5 ? object.marginTP.p5.toString().replace('.', ',') : null,
        p6: object?.marginTP?.p6 ? object.marginTP.p6.toString().replace('.', ',') : null
      });
    } else {
      this.signerForm.get('calculateMargins.marginTE').patchValue({
        p1: object?.marginTE?.p1 ? object.marginTE.p1.toString().replace('.', ',') : null,
        p2: object?.marginTE?.p2 ? object.marginTE.p2.toString().replace('.', ',') : null,
        p3: object?.marginTE?.p3 ? object.marginTE.p3.toString().replace('.', ',') : null,
        p4: object?.marginTE?.p4 ? object.marginTE.p4.toString().replace('.', ',') : null
      });
    }
  }

  private getCampaigns(): void {
    const url = this.isUserSpanish ? 'contrato.getCampaignsMultipoint' : 'contrato.getCampaignsMultipointPT';
    const params: MultipointGetCampaignRequest = { idBorrador: this.idDraft };
    if (this.signerForm.get('invoiceDate').value) {
      params.FechaVigenciaFutura = this.utilService.formatDate(this.signerForm.get('invoiceDate').value);
    }
    this.newContractPymesMultipointService.getCampaignsMultipoint(url, params).pipe(takeUntil(this.destroyed$)).subscribe(
      res => {
        if (res.success) {
          this.campaignList = res.data.map(value => ({ label: value.nombreCampania, value: value.codCampania }));
          this.campaignRes = res.data;
          const campaignValue = this.signerForm.get('campaign').value;
          const campaignDetail = this.campaignRes.find(value => value.codCampania === campaignValue);
          this.passThrough = campaignDetail?.passThrough;
          this.fijoFlex = campaignDetail?.fijoFlex;
          if (campaignValue && this.signerForm.get('campaignBase').value) {
            this.isFixedPrice = campaignDetail?.precioFijo;
            if (this.signerForm.get('marginIndicator').value !== '1') {
              this.setValidatorFieldRequired(this.signerForm.get('calculateMargins.marginTE'));
              this.setValidatorFieldRequired(this.signerForm.get('calculateMargins.marginTP'));
              this.signerForm.get('marginCups').setValidators(Validators.required);
              this.signerForm.get('marginCups').updateValueAndValidity();
              this.checkFee(this.signerForm.get('marginCups').value);
            }
            let campaignCode = null;
            if (this.isUserSpanish) {
              campaignCode = campaignDetail?.codCampaña;
            } else {
              campaignCode = campaignDetail?.idCampania;
            }
            this.getCampaignDetail(campaignCode);
          } else if (campaignValue) {
            this.getLevelList(campaignValue);
          }
          this.subscribeControls();
          this.emitCanGoNext();
          if (!this.isUserSpanish) {
            this.enablePayment(campaignDetail?.formPago === '300000008');
            this.showBankAccount(
              (campaignDetail?.formPago !== '300000007' && campaignDetail?.formPago !== '300000008') ||
              (campaignDetail?.formPago === '300000008' && this.signerForm.get('payment').value === '0')
            );
          }
          this.changeLoader.emit(false);
        } else {
          this.subscribeControls();
          this.emitCanGoNext();
          this.utilService.toastErrors(res);
          this.changeLoader.emit(false);
        }
      }, error => {
        this.subscribeControls();
        this.emitCanGoNext();
        this.utilService.toastErrors(error);
        this.changeLoader.emit(false);
      }
    );
  }

  private getCampaignDetail(idCampaign): void {
    this.changeLoader.emit(true);
    this.campaignsService.getCampaignDetail(idCampaign).pipe(takeUntil(this.destroyed$)).subscribe(
      (response: GenericResponse<CampaignDetail[]>) => {
        if (response.success) {
          this.campaignDetail = response.data[ 0 ];
          if(this.campaignDetail.zonasGeograficas[this.geographicIndexTab].tarifas[0].pRef_P1) {
            this.fijoFlexSimple = true;
          } else {
            this.fijoFlexSimple = false;
          }
          if (this.geographicZones.length === 0) {
            this.geographicZones = this.campaignDetail.zonasGeograficas.map(zone => zone.nombreZona);
          }
          this.campaignDetail.zonasGeograficas.find(zone => this.geographicZones[ this.geographicIndexTab ] === zone.nombreZona).tarifas.forEach(value =>
            Object.keys(value).forEach(key => {
              if (value[ key ] === null) {
                value[ key ] = null;
              }
            })
          );
        } else {
          this.utilService.toastErrors(response);
        }
        this.calculatedDone();
        this.changeLoader.emit(false);
      }, error => {
        this.changeLoader.emit(false);
        this.utilService.toastErrors(error);
      });
  }

  private enablePayment(enable: boolean): void {
    enable ? this.signerForm.get('payment').enable() : this.signerForm.get('payment').disable();
  }

  private showBankAccount(show: boolean): void {
    this.showBankAccountFields = show;
    const enableBankAccount = this.signerForm.get('payment').value === '0';
    const bankAccountLength = this.signerForm.get('sameBankAccount').value !== '0' ? this.installations.length : 1;
    for(let index = 0; index < bankAccountLength; index ++) {
      enableBankAccount ? this.signerForm.get('ibanCode' + '_' + index)?.enable() : this.signerForm.get('ibanCode' + '_' + index).disable();
      enableBankAccount ? this.signerForm.get('ibanEntity' + '_' + index)?.enable() : this.signerForm.get('ibanEntity' + '_' + index).disable();
      enableBankAccount ? this.signerForm.get('ibanOffice' + '_' + index)?.enable() : this.signerForm.get('ibanOffice' + '_' + index).disable();
      enableBankAccount ? this.signerForm.get('ibanDC' + '_' + index)?.enable() : this.signerForm.get('ibanDC' + '_' + index).disable();
      enableBankAccount ? this.signerForm.get('ibanAccount' + '_' + index)?.enable() : this.signerForm.get('ibanAccount' + '_' + index).disable();
      
      if (show) {
        this.signerForm.get('ibanCode' + '_' + index)?.setValidators([ Validators.required, Validators.minLength(4), Validators.maxLength(4) ]);
        this.signerForm.get('ibanEntity' + '_' + index)?.setValidators([ Validators.required, Validators.minLength(4), Validators.maxLength(4) ]);
        this.signerForm.get('ibanOffice' + '_' + index)?.setValidators([ Validators.required, Validators.minLength(4), Validators.maxLength(4) ]);
        this.signerForm.get('ibanDC' + '_' + index)?.setValidators([ Validators.required, Validators.minLength(2), Validators.maxLength(2) ]);
        this.signerForm.get('ibanAccount' + '_' + index)?.setValidators([ Validators.required, Validators.minLength(11), Validators.maxLength(11) ]);
      } else {
        this.signerForm.get('ibanCode' + '_' + index)?.setValidators(null);
        this.signerForm.get('ibanEntity' + '_' + index)?.setValidators(null);
        this.signerForm.get('ibanOffice' + '_' + index)?.setValidators(null);
        this.signerForm.get('ibanDC' + '_' + index)?.setValidators(null);
        this.signerForm.get('ibanAccount' + '_' + index)?.setValidators(null);
      }
      
      this.signerForm.get('ibanCode' + '_' + index)?.updateValueAndValidity();
      this.signerForm.get('ibanEntity' + '_' + index)?.updateValueAndValidity();
      this.signerForm.get('ibanOffice' + '_' + index)?.updateValueAndValidity();
      this.signerForm.get('ibanDC' + '_' + index)?.updateValueAndValidity();
      this.signerForm.get('ibanAccount' + '_' + index)?.updateValueAndValidity();
    };
    this.signerForm.setValidators(show ? this.validateIBANControlsPT.bind(this) : null);
    this.signerForm.updateValueAndValidity();
  }

  public handleSelectCampaign(event: string): void {
    this.installations?.forEach(installation => {
      installation.enterMargins = null;
    });
    const campaign = this.campaignRes.find(value => value.codCampania === event);
    this.signerForm.get('campaignBase').setValue(campaign?.campanaBase);
    this.geographicIndexTab = 0;
    this.signerForm.get('level').disable();
    this.signerForm.get('level').setValue(null);
    this.showCalculate = false;
    this.levelList = [];
    this.calculates = [];
    this.signerForm.patchValue({
      unitsTE: '0',
      unitsTP: '0',
      marginCups: null
    });
    this.signerForm.get('marginCups').clearValidators();
    this.signerForm.get('marginIndicator').setValue('1', { emitEvent: false });
    this.signerForm.get('marginCups').updateValueAndValidity();
    this.resetAllFields();
    this.passThrough = this.campaignRes.find(item => item.codCampania === campaign.codCampania)?.passThrough;
    if (!this.isUserSpanish) {
      this.signerForm.get('payment').setValue(campaign?.formPago === '300000007' ? '1' : '0', { emitEvent: false });
      this.enablePayment(campaign?.formPago === '300000008');
      event ? this.signerForm.get('sameBankAccount').enable() : this.signerForm.get('sameBankAccount').disable();
      this.showBankAccount(
        (campaign?.formPago !== '300000007' && campaign?.formPago !== '300000008') ||
        (campaign?.formPago === '300000008' && this.signerForm.get('payment').value === '0')
      );
    }
    this.fijoFlex = this.campaignRes.find(item => item.codCampania === campaign.codCampania)?.fijoFlex;
    if (this.signerForm.get('campaignBase').value) {
      this.checkValidators(this.signerForm.get('marginIndicator').value);
      this.isFixedPrice = campaign?.precioFijo;
      let campaignCode = null;
      if (this.isUserSpanish) {
        campaignCode = campaign.codCampania;
      } else {
        campaignCode = campaign.idCampania;
      }
      this.getCampaignDetail(campaignCode);
    } else {
      this.isFixedPrice = null;
      this.signerForm.get('campaignBase').setValue(null);
      this.getLevelList(event);
    }
  }

  private getLevelList(event: string): void {
    this.signerForm.get('level').enable();
    if (!this.isUserSpanish) {
      this.campaignRes.forEach(res => {
        if (res.codCampania === event) {
          this.levelList = res.niveles.map(value => ({ label: value.nombreNivel, value: value.nombreNivel }));
        }
      });
    } else {
      this.getCampaignLevelsFees(event);
    }
  }

  private getCampaignLevelsFees(cod): void {
    this.changeLoader.emit(true);
    this.campaignsService.getCampaignLevels(cod).pipe(takeUntil(this.destroyed$)).subscribe(
      response => {
        if (response.success) {
          this.levelList = response.data[0].niveles.map(value => ({ label: value.nombreNivel, value: value.nombreNivel }));
        }
        this.changeLoader.emit(false);
      }, error => {
        this.changeLoader.emit(false);
        this.utilService.toastErrors(error);
      }
    );
  }

  private subscribeControls(): void {
    this.signerForm.get('marginIndicator').valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(value => {
      this.resetAllFields();
      this.checkValidators(value);
      this.calculates = [];
      this.showCalculate = false;
    });
    this.signerForm.get('marginCups').valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(value => {
      if (this.signerForm.get('marginIndicator').value === '2') {
        this.checkFee(value);
      }
    });
    this.signerForm.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(() => {
      this.emitCanGoNext();
    });
  }

  private checkValidators(radio: string): void {
    if (radio === '1') {
      this.signerForm.get('marginCups').setValue(null);
      this.signerForm.get('marginCups').clearValidators();
    } else {
      this.setValidatorFieldRequired(this.signerForm.get('calculateMargins.marginTE'));
      this.setValidatorFieldRequired(this.signerForm.get('calculateMargins.marginTP'));
      this.signerForm.get('marginCups').setValidators(Validators.required);
    }
    this.signerForm.get('marginCups').updateValueAndValidity();
  }

  private resetAllFields(): void {
    this.resetField(this.signerForm.get('enterMargins'));
    this.resetField(this.signerForm.get('calculateMargins.marginTE'));
    this.signerForm.get('enterMargins').updateValueAndValidity();
    this.signerForm.get('calculateMargins.marginTE').updateValueAndValidity();
    if (this.isUserSpanish) {
      this.resetField(this.signerForm.get('calculateMargins.marginTP'));
      this.signerForm.get('calculateMargins.marginTP').updateValueAndValidity();
    }
  }

  private checkFee(marginCups: string): void {
    const currentInstallation = this.installations.find(val => val.cups === marginCups);
    if (currentInstallation?.fee === '2.0TD') {
      this.signerForm.get('calculateMargins.marginTE').patchValue({
        p4: 0,
        p5: 0,
        p6: 0
      });
      this.signerForm.get('calculateMargins.marginTP').patchValue({
        p3: 0,
        p4: 0,
        p5: 0,
        p6: 0
      });
      this.signerForm.get('calculateMargins.marginTE.p4').disable();
      this.signerForm.get('calculateMargins.marginTE.p5').disable();
      this.signerForm.get('calculateMargins.marginTE.p6').disable();
      this.signerForm.get('calculateMargins.marginTP.p3').disable();
      this.signerForm.get('calculateMargins.marginTP.p4').disable();
      this.signerForm.get('calculateMargins.marginTP.p5').disable();
      this.signerForm.get('calculateMargins.marginTP.p6').disable();
    } else {
      this.signerForm.get('calculateMargins.marginTE.p4').enable();
      if (this.isUserSpanish) {
        this.signerForm.get('calculateMargins.marginTE.p5').enable();
        this.signerForm.get('calculateMargins.marginTE.p6').enable();
        this.signerForm.get('calculateMargins.marginTP.p3').enable();
        this.signerForm.get('calculateMargins.marginTP.p4').enable();
        this.signerForm.get('calculateMargins.marginTP.p5').enable();
        this.signerForm.get('calculateMargins.marginTP.p6').enable();
      }
    }
  }

  private emitCanGoNext(): void {
    this.saveInstallationsPayment();
    const validSecondRadio = this.resultCalcTE >= 0 && this.resultCalcTP >= 0 && this.commission >= 0 && this.showCalculate;
    const validCampaignBase = this.signerForm.get('marginIndicator').value === '2' ? validSecondRadio : this.enterMarginsValid;
    const signerFormRawValue = this.signerForm.getRawValue();
    if (!this.isUserSpanish) {
      signerFormRawValue.contactMobile =
        signerFormRawValue.contactMobileCountryCode + signerFormRawValue.contactMobile;
    }
    this.data.emit({
      canGoNext: this.signerForm.valid && (this.signerForm.get('campaignBase').value ? validCampaignBase : true),
      campaignLabel: this.campaignList?.find((res: Select) => res.value === this.signerForm.get('campaign').value)?.label,
      isMultibanco: this.signerForm.get('payment')?.value === '1',
      ...signerFormRawValue
    });
  }

  private saveInstallationsPayment(): void {
    this.installations?.forEach((installation, index) => {
      const formIndex = this.signerForm.get('sameBankAccount').value !== '0' ? index : 0;
      installation.ibanCode = this.signerForm.get('ibanCode' + '_' + formIndex)?.value ? this.signerForm.get('ibanCode' + '_' + formIndex)?.value : installation.ibanCode;
      installation.ibanEntity = this.signerForm.get('ibanEntity' + '_' + formIndex)?.value ? this.signerForm.get('ibanEntity' + '_' + formIndex)?.value : installation.ibanEntity;
      installation.ibanOffice = this.signerForm.get('ibanOffice' + '_' + formIndex)?.value ? this.signerForm.get('ibanOffice' + '_' + formIndex)?.value : installation.ibanOffice;
      installation.ibanDC = this.signerForm.get('ibanDC' + '_' + formIndex)?.value ? this.signerForm.get('ibanDC' + '_' + formIndex)?.value : installation.ibanDC;
      installation.ibanAccount = this.signerForm.get('ibanAccount' + '_' + formIndex)?.value ? this.signerForm.get('ibanAccount' + '_' + formIndex)?.value : installation.ibanAccount;
    });
    this.installationData.emit({ installations: this.installations});
  }

  public handleCheckbox(checked: boolean): void {
    if (checked) {
      this.signerForm.patchValue({
        contactName: this.contact.contactName,
        contactSurname: this.contact.contactSurname,
        contactDocumentNumber: this.contact.contactDocumentNumber,
        contactEmail: this.contact.contactEmail,
        contactMobile: this.contact.contactMobile
      });
      if (!this.isUserSpanish) {
        this.signerForm.get('contactMobileCountryCode').setValue(this.contact.contactMobileCountryCode);
      }
      this.signerForm.get('contactName').disable();
      this.signerForm.get('contactSurname').disable();
      this.signerForm.get('contactEmail').disable();
      this.signerForm.get('contactMobileCountryCode').disable();
      this.signerForm.get('contactMobile').disable();
      if (this.contact.contactDocumentNumber) {
        this.signerForm.get('contactDocumentNumber').disable();
      }
    } else {
      this.signerForm.get('contactName').enable();
      this.signerForm.get('contactSurname').enable();
      this.signerForm.get('contactDocumentNumber').enable();
      this.signerForm.get('contactEmail').enable();
      this.signerForm.get('contactMobileCountryCode').enable();
      this.signerForm.get('contactMobile').enable();
    }
  }

  private setValidatorFieldRequired(form): void {
    if (form) {
      Object.keys(form.controls).forEach(field => {
        if (field === 'marginTE') {
          form.get(field).setValidators([ Validators.required, Validators.min(0), Validators.max(this.isUserSpanish ? (this.greaterEqual100 ? 24.999 : 49.999) : 25.999), Validators.pattern('([0-9]{1,2}(,[0-9]{1,3})?)') ]);
        } else if (field === 'marginTP') {
          form.get(field).setValidators([ Validators.required, Validators.min(0), Validators.max(24.999), Validators.pattern('([0-9]{1,2}(,[0-9]{1,3})?)') ]);
        } else {
          form.get(field).setValidators([ Validators.required, Validators.pattern('([0-9]+(,[0-9]{1,6})?)') ]);
        }
        form.get(field).updateValueAndValidity();
      });
    }
  }

  private resetField(form: any): void {
    if (form) {
      Object.keys(form.controls).forEach(field => {
        form.get(field).reset();
        form.get(field).clearValidators();
        form.get(field).setValue(null);
        form.get(field).updateValueAndValidity();
      });
    }
  }

  public checkHaveError(key: string): boolean {
    return this.signerForm?.get(key).invalid &&
      !(this.signerForm?.get(key).touched || this.signerForm?.get(key).dirty);
  }

  public checkRequiredError(key: string): boolean {
    return this.signerForm?.get(key).errors?.required &&
      (this.signerForm?.get(key).touched || this.signerForm?.get(key).dirty);
  }

  public checkPatternError(key: string): boolean {
    return this.signerForm?.get(key).errors?.pattern &&
      (this.signerForm?.get(key).touched || this.signerForm?.get(key).dirty);
  }

  public handleCalculate(): void {
    this.calculates = [];
    this.commission = 0;
    if (this.isFixedPrice) {
      if (this.signerForm.get('marginIndicator').value !== '1') {
        this.isUserSpanish ? this.calculateMarginPrice() : this.calculateMarginPricePT();
      }
    } else {
      this.calculateMarginIndexed();
    }
    this.emitCanGoNext();
  }

  private calculateMarginPrice(): void {
    const results = this.calculateMarginTETP();
    this.campaignDetail.zonasGeograficas.find(zone => this.geographicZones[ this.geographicIndexTab ] === zone.nombreZona).tarifas.forEach(value => {
      const resultsTP = [];
      const resultsTE = [];
      const campaignsTP = this.getPotencies('tP_P', value);
      const campaignsTe = this.getPotencies('precioFinal_P', value);
      const campaignsTM = this.getPotencies('tM_P', value);
      campaignsTP.forEach((valueTP, index) => {
        if (value.nombreTarifa === '2.0TD' && index > 1) {
          resultsTP.push(null);
        } else {
          const calc = valueTP + results[ 1 ];
          resultsTP.push(parseFloat(calc.toString().match(/^-?\d+(?:\.\d{0,6})?/)[ 0 ]));
        }
      });
      campaignsTe.forEach((valueFinalPrice, index) => {
        if (value.nombreTarifa === '2.0TD' && index > 2) {
          resultsTE.push(null);
        } else {
          const calc = (valueFinalPrice / 1000) + ((results[ 0 ] / 1000) * campaignsTM[index]);
          resultsTE.push(parseFloat(calc.toString().match(/^-?\d+(?:\.\d{0,6})?/)[ 0 ]));
        }
      });
      this.calculates.push({
        name: value.nombreTarifa,
        te: resultsTE,
        tp: resultsTP,
      });
    });
    this.canSettlementsCommission();
    this.showCalculate = true;
  }

  private calculateMarginPricePT(): void {
    const results = this.calculateMarginTETPPT();
    this.campaignDetail.zonasGeograficas.find(zone => this.geographicZones[ this.geographicIndexTab ] === zone.nombreZona).tarifas.forEach(value => {
      const resultsTE = [];
      let campaignsTe = null;
      if (this.isFixedPrice) {
        campaignsTe = this.getPotencies('precio_P', value);
      } else {
        campaignsTe = this.getPotencies('precioFinal_P', value);
      }
      const campaignsTM = this.getPotencies('tM_P', value);
      campaignsTe.forEach((valueFinalPrice, index) => {
        if (value.nombreTarifa === '2.0TD' && index > 2) {
          resultsTE.push(null);
        } else {
          const calc = (valueFinalPrice / 1000) + ((results[ 0 ] / 1000) * campaignsTM[index]);
          resultsTE.push(parseFloat(calc.toString().match(/^-?\d+(?:\.\d{0,6})?/)[ 0 ]));
        }
      });
      this.calculates.push({
        name: value.nombreTarifa,
        te: resultsTE,
        tp: [],
      });
    });
    this.canSettlementsCommission();
    this.showCalculate = true;
  }

  private calculateMarginTETP(): number[] {
    const currentInstallation = this.installations.find(val => val.cups === this.signerForm.get('marginCups').value);
    const feeList = this.campaignDetail.zonasGeograficas.find(zone => this.geographicZones[ this.geographicIndexTab ] === zone.nombreZona).tarifas;
    const campaign = feeList.find(val2 => val2.nombreTarifa === currentInstallation.fee);
    const campaignsTP = this.getPotencies('tP_P', campaign);
    const campaignsFinalPrice = this.getPotencies('precioFinal_P', campaign);
    const inputsTE = this.pushInputTETP(this.signerForm.get('calculateMargins.marginTE'));
    const inputsTP = this.pushInputTETP(this.signerForm.get('calculateMargins.marginTP'));
    const sumTicketTE = [];
    const sumTicketAccionaTE = [];
    const sumTicketTP = [];
    const sumTicketAccionaTP = [];
    for (let i = 0; i < 6; i++) {
      const calcSelectTE = this.signerForm.get('unitsTE').value !== '0' ?
        inputsTE[ i ] / this.signerForm.get('unitsTE').value : inputsTE[ i ];
      const calcSelectTP = this.signerForm.get('unitsTP').value !== '0' ?
        inputsTP[ i ] * this.signerForm.get('unitsTP').value : inputsTP[ i ];
      const calcTicketTE = calcSelectTE * currentInstallation.estimatedConsumptionPeriod[ i ];
      const calcTicketAccionaTE = (campaignsFinalPrice[ i ] / 1000) * currentInstallation.estimatedConsumptionPeriod[ i ];
      const calcTicketTP = calcSelectTP * currentInstallation.contractedPowerPeriod[ i ];
      const calcTicketAccionaTP = campaignsTP[ i ] * currentInstallation.contractedPowerPeriod[ i ];
      sumTicketTE.push(calcTicketTE);
      sumTicketAccionaTE.push(calcTicketAccionaTE);
      sumTicketTP.push(calcTicketTP);
      sumTicketAccionaTP.push(calcTicketAccionaTP);
    }
    const ticketTE = sumTicketTE.reduce((total, val) => total + val, 0);
    const ticketAccionaTE = sumTicketAccionaTE.reduce((total, val) => total + val, 0);
    const ticketTP = sumTicketTP.reduce((total, val) => total + val, 0);
    const ticketAccionaTP = sumTicketAccionaTP.reduce((total, val) => total + val, 0);
    const sumRowEstimatedConsumption = currentInstallation.estimatedConsumptionPeriod.reduce((total, val) => total + val, 0);
    const sumRowContractPotency = currentInstallation.contractedPowerPeriod.reduce((total, val) => total + val, 0);
    const resultCalcTE = (((ticketTE - ticketAccionaTE) / sumRowEstimatedConsumption) * 1000) / 1.015;
    const resultCalcTP = (ticketTP - ticketAccionaTP) / sumRowContractPotency;
    // this.installations?.forEach(installation => {
    //   const installationEstimatedConsumption = installation.estimatedConsumptionPeriod.reduce((total, val) => total + val, 0);
    //   const installatioContractPotency = installation.contractedPowerPeriod.reduce((total, val) => total + val, 0);
    //   const commissionTE = ((((installationEstimatedConsumption / 1000) * parseFloat(installation.energyChannelCommission)) / 100)
    //     * resultCalcTE);
    //   const commissionTP = (((installatioContractPotency * parseFloat(installation.commissionChannelPower)) / 100) * resultCalcTP);
    //   this.commission += parseFloat(String(commissionTE + commissionTP).match(/^-?\d+(?:\.\d{0,2})?/)[ 0 ]);
    // });
    this.resultCalcTE = parseFloat(resultCalcTE.toString().match(/^-?\d+(?:\.\d{0,6})?/)[ 0 ]);
    this.resultCalcTP = parseFloat(resultCalcTP.toString().match(/^-?\d+(?:\.\d{0,6})?/)[ 0 ]);
    this.signerForm.get('enterMargins').patchValue({
      marginTE: this.resultCalcTE,
      marginTP: this.resultCalcTP
    });
    return [ resultCalcTE, resultCalcTP ];
  }

  private calculateMarginTETPPT(): number[] {
    const currentInstallation = this.installations.find(val => val.cups === this.signerForm.get('marginCups').value);
    const feeValue = currentInstallation?.tension + ' - ' + currentInstallation?.cycleName?.split(' - ')[ 0 ] + ' - ' + currentInstallation?.periodName;
    const feeList = this.campaignDetail.zonasGeograficas.find(zone => this.geographicZones[ this.geographicIndexTab ] === zone.nombreZona).tarifas;
    const campaign = feeList.find(val2 => val2.nombreTarifa === feeValue);
    const campaignsFinalPrice = this.getPotencies('precioFinal_P', campaign);
    const inputsTE = this.pushInputTETP(this.signerForm.get('calculateMargins.marginTE'));
    const sumTicketTE = [];
    const sumTicketAccionaTE = [];
    for (let i = 0; i < 4; i++) {
      const calcSelectTE = this.signerForm.get('unitsTE').value !== '0' ?
        inputsTE[ i ] / this.signerForm.get('unitsTE').value : inputsTE[ i ];
      if (currentInstallation.estimatedConsumptionPeriod) {
        const calcTicketTE = calcSelectTE * currentInstallation.estimatedConsumptionPeriod[ i ];
        const calcTicketAccionaTE = (campaignsFinalPrice[ i ] / 1000) * currentInstallation.estimatedConsumptionPeriod[ i ];
        sumTicketTE.push(calcTicketTE);
        sumTicketAccionaTE.push(calcTicketAccionaTE);
      } else {
        sumTicketTE.push(0);
        sumTicketAccionaTE.push(0);
      }
    }
    const ticketTE = sumTicketTE?.reduce((total, val) => total + val, 0);
    const ticketAccionaTE = sumTicketAccionaTE?.reduce((total, val) => total + val, 0);
    const sumRowEstimatedConsumption = currentInstallation.estimatedConsumptionPeriod?.reduce((total, val) => total + val, 0);
    const resultCalcTE = (((ticketTE - ticketAccionaTE) / sumRowEstimatedConsumption) * 1000) / 1.015 || 0;
    // this.installations?.forEach(installation => {
    //   const installationEstimatedConsumption = installation.estimatedConsumptionPeriod?.reduce((total, val) => total + val, 0);
    //   const commissionTE = ((((installationEstimatedConsumption / 1000) * parseFloat(installation.energyChannelCommission)) / 100)
    //     * resultCalcTE) || 0;
    //   this.commission += parseFloat(String(commissionTE).match(/^-?\d+(?:\.\d{0,2})?/)[ 0 ]);
    // });
    this.resultCalcTE = parseFloat(resultCalcTE.toString().match(/^-?\d+(?:\.\d{0,6})?/)[ 0 ]);
    this.signerForm.get('enterMargins').patchValue({
      marginTE: this.resultCalcTE,
      marginTP: 0
    });
    return [ resultCalcTE, 0 ];
  }


  private calculateMarginIndexed(): void {
    this.campaignDetail.zonasGeograficas.find(zone => this.geographicZones[ this.geographicIndexTab ] === zone.nombreZona).tarifas.forEach(value => {
      const resultsAI = [];
      let resultsTP = [];
      const campaignsAI = this.getPotencies('ai_P', value);
      const resultsKI = [];
      const campaignsKI = this.getPotencies('ki_P', value);
      const campaignsTP = this.getPotencies('tP_P', value);
      const campaignsTe = this.getPotencies('tE_P', value);
      const campaignsCPM = this.getPotencies('cpM_P', value);
      const campaignsCTM = this.getPotencies('ctM_P', value);
      campaignsAI.forEach((valueAI, index) => {
        if (value.nombreTarifa === '2.0TD' && index > 2) {
          resultsAI.push(null);
        } else {
          const calc = ((this.signerForm.get('enterMargins.marginsTE').value.replace(',', '.') / 1000) * ((1 + campaignsCTM[index]) * (1 + campaignsCPM[index]))) +
            (valueAI / 1000) + (campaignsTe[ index ] / 1000);
          resultsAI.push(parseFloat(calc.toString().match(/^-?\d+(?:\.\d{0,6})?/)[ 0 ]));
        }
      });
      campaignsKI.forEach((valueKI, index) => {
        if (value.nombreTarifa === '2.0TD' && index > 2) {
          resultsKI.push(null);
        } else {
          resultsKI.push(parseFloat(valueKI.toString().match(/^-?\d+(?:\.\d{0,6})?/)[ 0 ]));
        }
      });
      resultsTP = this.isUserSpanish ? this.getResutsTP(value.nombreTarifa, campaignsTP) : [];
      this.calculates.push({
        name: value.nombreTarifa,
        ai: resultsAI,
        ki: resultsKI,
        tp: resultsTP
      });
    });
    this.canSettlementsCommission();
    this.showCalculate = true;
  }

  private getResutsTP(fee: string, campaigns): number[] {
    const resultsTP = [];
    campaigns.forEach((valueTP, index) => {
      if (fee === '2.0TD' && index > 1) {
        resultsTP.push(null);
      } else {
        const calc = parseFloat(this.signerForm.get('enterMargins.marginTP').value.replace(',', '.')) + valueTP;
        resultsTP.push(parseFloat(calc.toString().match(/^-?\d+(?:\.\d{0,6})?/)[ 0 ]));
      }
    });
    return resultsTP;
  }

  private pushInputTETP(form: any): any {
    const inputs = [];
    Object.keys(form.controls).forEach(field => {
      inputs.push(form.get(field).value ? form.get(field).value.replace(',', '.') : form.get(field).value);
    });
    return inputs;
  }

  private getPotencies(key: string, campaign: Fee): number[] {
    const aux = [];
    const pLenght = this.isUserSpanish ? 7 : 5;
    for (let i = 1; i < pLenght; i++) {
      aux.push(campaign[ `${key}${i}` ] ? campaign[ `${key}${i}` ] : 0);
    }
    return aux;
  }

  private canSettlementsCommission(): void {
    if (!this.canSettlements && this.commission < 0) {
      this.toastrService.success('COMMONS.CALCULATES_INVALID');
    }
  }

  private mobileCountryCodeChange(value: string): void {
    this.signerForm.get('contactMobile').enable();
    if (value === '0034') {
      this.signerForm.get('contactMobile').setValidators(
        [ this.utilValidatorsService.validateMobile, Validators.maxLength(9) ]
      );
    } else if (value === '00351') {
      this.signerForm.get('contactMobile').setValidators(
        [ this.utilValidatorsService.validateMobilePT, Validators.maxLength(9) ]
      );
    }
    this.signerForm.get('contactMobile').updateValueAndValidity({ onlySelf: true });
  }

  private getMobileAndCountryCode(value: string | null): Mobile {
    const result: Mobile = { mobile: value };
    if (value) {
      if (!this.isUserSpanish && value?.length > 9) {
        const countryCode = value.slice(0, value.length - 9);
        const validCountryCode = this.contactMobileCountryCodeList.find(code => code.value === countryCode);
        if (validCountryCode) {
          result.countryCode = countryCode;
        }
      }
      result.mobile = value.slice(value.length - 9, value.length);
    }

    return result;
  }

  private initMobileCountryCodePT(): void {
    if (!this.isUserSpanish) {
      this.signerForm.get('contactMobileCountryCode').setValidators(Validators.required);
      this.i18NextService.events.languageChanged.pipe(takeUntil(this.destroyed$)).subscribe(() => {
        this.contactMobileCountryCodeList = this.utilService.getListCountryMobile();
      });
      this.signerForm.get('contactMobileCountryCode').valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(
        value => {
          if (value) {
            this.mobileCountryCodeChange(value);
          }
        }
      );
    }
  }

  private buildForm(): void {
    this.signerForm = new FormGroup({
      contactName: new FormControl(null, [ Validators.required, Validators.maxLength(50) ]),
      contactSurname: new FormControl(null, [ Validators.required, Validators.maxLength(50) ]),
      contactDocumentNumber: new FormControl('', [ this.utilValidatorsService.validateCIFNIF ]),
      contactEmail: new FormControl(
        null,
        [ Validators.required, Validators.pattern(this.utilValidatorsService.emailPattern), Validators.maxLength(50) ]
      ),
      contactMobileCountryCode: new FormControl(null),
      contactMobile: new FormControl('', [ Validators.required, this.utilValidatorsService.validateMobile, Validators.maxLength(9) ]),
      campaign: new FormControl(null, Validators.required),
      level: new FormControl({ value: null, disabled: true }, Validators.required),
      marginIndicator: new FormControl('1'),
      calculateMargins: this.calculateMargins(),
      enterMargins: this.enterMargins(),
      unitsTE: new FormControl(null),
      unitsTP: new FormControl(null),
      campaignBase: new FormControl(false),
      marginCups: new FormControl(null),
      radioDate: new FormControl('1'),
      invoiceDate: new FormControl(null),
      payment: new FormControl({ value: null, disabled: true }, this.isUserSpanish ? null : Validators.required),
      sameBankAccount: new FormControl({ value: '0', disabled: true }),
    });
  }

  private subscribeRadioDateChange(): void {
    this.signerForm.get('radioDate').valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(value => {
      this.signerForm.get('invoiceDate').clearValidators();
      this.signerForm.get('invoiceDate').reset();
      this.signerForm.get('campaign').reset();
      this.signerForm.get('campaignBase').reset();
      this.signerForm.get('level').reset();
      if (value === '2') {
        this.enterMarginsValid = false;
        this.signerForm.get('invoiceDate').setValidators(Validators.required);
        this.signerForm.get('campaign').disable();
        this.signerForm.get('level').disable();
      } else {
        this.signerForm.get('campaign').enable();
        this.signerForm.get('level').disable();
        this.getCampaigns();
      }
      this.signerForm.get('payment').setValue('0');
      this.enablePayment(false);
      this.signerForm.get('sameBankAccount').disable();
      this.showBankAccount(false);
    });
    this.signerForm.get('invoiceDate').valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(value => {
      if (value) {
        this.changeLoader.emit(true);
        this.getCampaigns();
        this.signerForm.get('campaign').enable();
      }
    });
  }

  public changeGeographicTab(tabIndex: number): void {
    this.geographicIndexTab = tabIndex;
    this.handleCalculate();
  }

  public getEnterMargins(form: FormGroup): void {
    if (this.enterMarginsValid !== form?.valid) {
      this.enterMarginsValid = form?.valid;
      this.emitCanGoNext();
    }
    this.installations?.forEach(installation => {
      let feeName = !this.isUserSpanish ?
        (installation?.periodName?.toString()?.length > 0 ? installation?.periodName : installation?.feeName?.split(' - ')?.pop()) :
        installation?.fee;
      if (feeName?.includes('S/C - ')) {
        feeName = feeName.replace('S/C - ', '');
      }  else if (feeName?.includes('3.')) {
        feeName = '3.0TD';
      } else if (feeName?.includes('6.')) {
        feeName = '6.XTD';
      }
      const formGroup = form?.controls[feeName];
      if (formGroup?.valid) {
        installation.enterMargins = {
          marginTP: form.get('marginTP')?.value?.toString().replace(',', '.'),
          marginsTE: {
            p1: formGroup?.get('marginTEP1')?.value?.toString().replace(',', '.'),
            p2: formGroup?.get('marginTEP2')?.value?.toString().replace(',', '.'),
            p3: formGroup?.get('marginTEP3')?.value?.toString().replace(',', '.'),
            p4: formGroup?.get('marginTEP4')?.value?.toString().replace(',', '.'),
            p5: formGroup?.get('marginTEP5')?.value?.toString().replace(',', '.'),
            p6: formGroup?.get('marginTEP6')?.value?.toString().replace(',', '.'),
          }
        };
        if (!this.isUserSpanish) {
          delete installation.enterMargins.marginTP;
        }
      }
    });
    this.installationData.emit({ installations: this.installations});
  }

  public checkControlRequired(controlName: string, index: number): boolean {
    return this.signerForm.get(controlName)?.errors?.required &&
      (this.signerForm.get(controlName)?.touched || this.signerForm.get(controlName)?.dirty) && ! this.checkControlIban(index);
  }

  public checkControlIban(index: number): boolean {
    let keysObject = Object.keys(this.signerForm.controls)
      .filter(val => val.startsWith('iban'))
      .filter(val => val.includes(index.toString()));
    const haveValues = keysObject.some(key => !!this.signerForm.get(key).value);
    const touchedOrDirty = keysObject.some(key => this.signerForm.get(key).touched || this.signerForm.get(key).dirty);
    return haveValues && this.signerForm.hasError('invalid_IBAN') && touchedOrDirty;
  }

  public onPasteIBAN(event: ClipboardEvent, index: number): void {
    const clipboardText = event.clipboardData?.getData('text') ?? '';
    const pasteIBAN: string = clipboardText.replace(/\s/g, '');
    const target = event.target as HTMLInputElement;

    if (pasteIBAN.length === 25) {
      event.preventDefault();
      this.signerForm.get('ibanCode' + '_' + index).patchValue(pasteIBAN.slice(0, 4).toUpperCase());
      this.signerForm.get('ibanEntity' + '_' + index).patchValue(pasteIBAN.slice(4, 8).replace(/[^0-9]/g, ''));
      this.signerForm.get('ibanOffice' + '_' + index).patchValue(pasteIBAN.slice(8, 12).replace(/[^0-9]/g, ''));
      this.signerForm.get('ibanAccount' + '_' + index).patchValue(pasteIBAN.slice(12, 23).replace(/[^0-9]/g, ''));
      this.signerForm.get('ibanDC' + '_' + index).patchValue(pasteIBAN.slice(23, 25).replace(/[^0-9]/g, ''));
      // Marcar como touched
      this.signerForm.get('ibanCode' + '_' + index).markAsTouched();
      this.signerForm.get('ibanEntity' + '_' + index).markAsTouched();
      this.signerForm.get('ibanOffice' + '_' + index).markAsTouched();
      this.signerForm.get('ibanAccount' + '_' + index).markAsTouched();
      this.signerForm.get('ibanDC' + '_' + index).markAsTouched();
      this.signerForm.updateValueAndValidity();
      return;
    }

    if (target && pasteIBAN.length > 0 && pasteIBAN.length <= Number(target.maxLength)) {
      event.preventDefault();
      const controlName = target.getAttribute('formControlName');
      if (controlName) {
        let cleanedValue = pasteIBAN;
        if (controlName.startsWith('ibanCode')) {
          cleanedValue = pasteIBAN.replace(/[^A-Z0-9]/g, '').toUpperCase();
        } else {
          cleanedValue = pasteIBAN.replace(/[^0-9]/g, '');
        }
        this.signerForm.get(controlName)?.setValue(cleanedValue);
        this.signerForm.get(controlName)?.markAsTouched();
        this.signerForm.updateValueAndValidity();
      }
    }
  }

  public bankAccountKeyUp(
    event: KeyboardEvent,
    previousInputId: string | null,
    currentInputId: string,
    nextInputId: string | null,
    index: number
  ): void {
    const previousInput = this.getInputElement(previousInputId + '_' + index);
    const currentInput = this.getInputElement(currentInputId + '_' + index);
    const nextInput = this.getInputElement(nextInputId + '_' + index);
    
    // Mapear del ID del input al nombre del FormControl
    const idToControlName: { [key: string]: string } = {
      'ibanInput': 'ibanCode',
      'entityInput': 'ibanEntity',
      'officeInput': 'ibanOffice',
      'accountInput': 'ibanAccount',
      'dcInput': 'ibanDC'
    };
    const controlName = idToControlName[currentInputId] + '_' + index;
    
    if (currentInput) {
      let cleanedValue = currentInput.value.replace(/\s/g, '');
      
      // Restringir caracteres por tipo de campo
      if (currentInputId === 'ibanInput') {
        // Para IBAN: solo letras mayúsculas y dígitos
        cleanedValue = cleanedValue.replace(/[^A-Z0-9]/g, '').toUpperCase();
      } else {
        // Para otros campos: solo dígitos
        cleanedValue = cleanedValue.replace(/[^0-9]/g, '');
      }
      
      // Actualizar el FormControl
      this.signerForm.get(controlName)?.setValue(cleanedValue, { emitEvent: false });
      this.signerForm.get(controlName)?.markAsTouched();
      this.signerForm.updateValueAndValidity();
      currentInput.value = cleanedValue;
    }
    
    if (!event.ctrlKey && event.key !== 'Control') {
      if (currentInput && currentInput.selectionStart === currentInput.maxLength && nextInput) {
        nextInput.focus();
        nextInput.setSelectionRange(0, 0);
      } else if (
        (event.key === 'Backspace' || event.key === 'ArrowLeft')
        && currentInput
        && currentInput.selectionStart === 0
        && previousInput
      ) {
        previousInput.focus();
      }
    }
  }

  private getInputElement(id: string): HTMLInputElement | null {
    return id ? (document.getElementById(id) as HTMLInputElement) : null;
  }

  private validateIBANControlsPT(control: AbstractControl): ValidationErrors | null {
    const sameBankAccountValue = control.get('sameBankAccount')?.value;
    const bankAccountLength = sameBankAccountValue !== '0' ? this.installations?.length : 1;
    for(let index = 0; index < bankAccountLength; index ++) {
      const ibanCode = (control.get('ibanCode' + '_' + index)?.value ?? '').toString().replace(/\s/g, '');
      const entity = (control.get('ibanEntity' + '_' + index)?.value ?? '').toString().replace(/\s/g, '');
      const office = (control.get('ibanOffice' + '_' + index)?.value ?? '').toString().replace(/\s/g, '');
      const dc = (control.get('ibanDC' + '_' + index)?.value ?? '').toString().replace(/\s/g, '');
      const account = (control.get('ibanAccount' + '_' + index)?.value ?? '').toString().replace(/\s/g, '');
      const accountEmpty = ibanCode === '' && account === '' && entity === '' && office === '' && dc === '';

      if (!accountEmpty && !/^[A-Z]{2}\d{2}$/.test(ibanCode)) {
        return { invalid_IBAN: true };
      }

      if (!accountEmpty && !validateIBANPortugal(entity, office, dc, account)) {
        return { invalid_IBAN: true };
      }
    }
    return null;
  }

  private subscribePaymentChanges(): void {
    this.signerForm.get('payment').valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(value => {
      if (value === '1') {
        const bankAccountLength = this.signerForm.get('sameBankAccount').value !== '0' ? this.installations.length : 1;
        for(let index = 0; index < bankAccountLength; index ++) {
          this.signerForm.get('ibanCode' + '_' + index).reset();
          this.signerForm.get('ibanEntity' + '_' + index).reset();
          this.signerForm.get('ibanOffice' + '_' + index).reset();
          this.signerForm.get('ibanDC' + '_' + index).reset();
          this.signerForm.get('ibanAccount' + '_' + index).reset();
        };
      }
      this.showBankAccount(value === '0' && this.signerForm.get('campaign')?.value);
    });
  }

  private subscribeSameBankAccount(): void {
    this.signerForm.get('sameBankAccount').valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(value => {
      this.builPTBankAccountFields();
      this.showBankAccount(this.showBankAccountFields);
    });
  }
}
